import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Info } from 'lucide-react'
import { useState } from 'react'

import { adminRecordInspector } from 'rails_routes'
import { Select, TextLink } from 'slab'
import { useGetAdminEmployersEmployerId } from 'utils/web_api'

import DeductionExplanationModal from './DeductionExplanationModal'
import { type MoneyAmount } from '../../types'
import { getLocaleCurrencyString, getLocaleDateString } from '../../utils'
import { type GroupedConsistentDeduction, useGetMemberConsistentDeductions } from '../hooks/useGetMemberConsistentDeductions'
import TanstackTable from '../reusable/table/TanstackTable'
import { ThatchTooltip } from '../reusable/Tooltip'

type MemberConsistentDeductionsTableProps = {
  memberId: string
  employerId: string
  selectedYear: number
}

// Helper function to safely parse a monetary value
const safeParseMonetaryValue = (value: MoneyAmount | undefined | null): number => {
  if (!value || !value.value) return 0
  const parsed = parseFloat(value.value)
  return isNaN(parsed) ? 0 : parsed
}

// Helper function to format benefit enrollment state
// TODO(amanda): Modify this to more accurately show state across both pre+post-tax benefit enrollments
// and also unenrollments.
const formatBenefitEnrollmentState = (state: string | undefined): string => {
  if (!state) return ''

  // Convert snake_case to Title Case and replace underscores with spaces
  return state
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const columnHelper = createColumnHelper<GroupedConsistentDeduction>()

const MemberConsistentDeductionsTableImpl = ({ memberId, employerId, selectedYear }: MemberConsistentDeductionsTableProps) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDeduction, setSelectedDeduction] = useState<GroupedConsistentDeduction | null>(null)

  const { data, isLoading, error } = useGetMemberConsistentDeductions(memberId, selectedYear)

  const { data: employer, isLoading: employerLoading, error: employerError } = useGetAdminEmployersEmployerId(employerId)

  const openModal = (deduction: GroupedConsistentDeduction) => {
    setSelectedDeduction(deduction)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const columns = [
    columnHelper.accessor('payDate', {
      id: 'payDate',
      cell: (info) => {
        const payPeriod = info.row.original.payPeriodId
        return (
          <div className='h-12 flex items-center'>

            <TextLink
              href={adminRecordInspector.show({ id: payPeriod })}
              target='_blank'
            >
              {getLocaleDateString(new Date(info.getValue()))}
            </TextLink>
          </div>
        )
      },
      header: () => <div className='text-left text-sm font-semibold'>Pay Date</div>,
    }),
    columnHelper.accessor('payScheduleName', {
      id: 'payScheduleName',
      cell: (info) => (
        <div className='h-12 flex items-center'>
          {info.getValue() || '-'}
        </div>
      ),
      header: () => <div className='text-left text-sm font-semibold'>Pay Schedule</div>,
    }),
    columnHelper.accessor(row => row.pretaxDeduction?.status || '', {
      id: 'status',
      cell: (info) => {
        const pretaxStatus = info.row.original.pretaxDeduction?.status
        const posttaxStatus = info.row.original.posttaxDeduction?.status

        // Show the most important status (created > updated > corrected > unchanged)
        const status = getHighestPriorityStatus(pretaxStatus, posttaxStatus)

        if (status === 'unchanged') return null

        let classes = ''
        let label = ''

        switch (status) {
          case 'created':
            classes = 'bg-green-100 text-green-700'
            label = 'New'
            break
          case 'updated':
            classes = 'bg-orange-100 text-orange-800'
            label = 'Updated'
            break
          case 'corrected':
            classes = 'bg-purple-100 text-purple-800'
            label = 'Corrected'
            break
          default:
            return null
        }

        return (
          <div className='h-12 flex items-center justify-end'>
            <div className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}`}>
              {label}
            </div>
          </div>
        )
      },
      header: () => <div className='text-center text-sm font-semibold'>Status</div>,
    }),
    columnHelper.accessor(row => {
      const amount = row.pretaxDeduction?.amount
      return safeParseMonetaryValue(amount)
    }, {
      id: 'pretaxAmount',
      cell: (info) => {
        const value = info.getValue()
        const preTaxDeduction = info.row.original.pretaxDeduction
        const correction = preTaxDeduction?.correction
        const correctionValue = safeParseMonetaryValue(correction)

        return (
          <div className='h-12 flex items-center justify-end'>
            <TextLink
              href={adminRecordInspector.show({ id: preTaxDeduction?.id })}
              target='_blank'
            >
              {getLocaleCurrencyString(value)}
            </TextLink>
            {correctionValue !== 0 && (
              <span className='ml-1'>
                <ThatchTooltip
                  text={`Corrected: ${getLocaleCurrencyString(correctionValue)}`}
                  className='max-w-60'
                >
                  <Info className='w-4 h-4 text-gray-400' />
                </ThatchTooltip>
              </span>
            )}
          </div>
        )
      },
      header: () => <div className='text-right text-sm font-semibold'>Pre-tax Amount</div>,
    }),
    columnHelper.accessor(row => {
      const amount = row.posttaxDeduction?.amount
      return safeParseMonetaryValue(amount)
    }, {
      id: 'posttaxAmount',
      cell: (info) => {
        const value = info.getValue()
        const postTaxDeduction = info.row.original.posttaxDeduction
        const correction = postTaxDeduction?.correction
        const correctionValue = safeParseMonetaryValue(correction)

        return (
          <div className='h-12 flex items-center justify-end'>
            <TextLink
              href={adminRecordInspector.show({ id: postTaxDeduction?.id })}
              target='_blank'
            >
              {getLocaleCurrencyString(value)}
            </TextLink>
            {correctionValue !== 0 && (
              <span className='ml-1'>
                <ThatchTooltip
                  text={`Corrected: ${getLocaleCurrencyString(correctionValue)}`}
                  className='max-w-60'
                >
                  <Info className='w-4 h-4 text-gray-400' />
                </ThatchTooltip>
              </span>
            )}
          </div>
        )
      },
      header: () => <div className='text-right text-sm font-semibold'>Post-tax Amount</div>,
    }),
    columnHelper.accessor(row => {
      const pretaxPremiums = safeParseMonetaryValue(row.pretaxDeduction?.total_premiums)
      const posttaxPremiums = safeParseMonetaryValue(row.posttaxDeduction?.total_premiums)
      return Math.max(pretaxPremiums, posttaxPremiums)
    }, {
      id: 'totalPremiums',
      cell: (info) => {
        const value = info.getValue()
        return (
          <div className='h-12 flex items-center justify-end'>
            {getLocaleCurrencyString(value)}
          </div>
        )
      },
      header: () => <div className='text-right text-sm font-semibold'>Total Premiums</div>,
    }),
    columnHelper.accessor(row => {
      const pretaxContribution = safeParseMonetaryValue(row.pretaxDeduction?.employer_contribution)
      const posttaxContribution = safeParseMonetaryValue(row.posttaxDeduction?.employer_contribution)
      return Math.max(pretaxContribution, posttaxContribution)
    }, {
      id: 'employerContribution',
      cell: (info) => {
        const value = info.getValue()
        return (
          <div className='h-12 flex items-center justify-end'>
            {getLocaleCurrencyString(value)}
          </div>
        )
      },
      header: () => <div className='text-right text-sm font-semibold'>Employer Contribution</div>,
    }),
    columnHelper.accessor(row => {
      const pretaxState = row.pretaxDeduction?.benefit_enrollment?.state
      const posttaxState = row.posttaxDeduction?.benefit_enrollment?.state

      // Prefer the pretax state if available, otherwise use posttax state
      return pretaxState || posttaxState
    }, {
      id: 'finchStatus',
      cell: (info) => {
        const state = info.getValue()
        return (
          <div className='h-12 flex items-center justify-end'>
            {state ? formatBenefitEnrollmentState(state) : '-'}
          </div>
        )
      },
      header: () => <div className='text-right text-sm font-semibold'>Finch Status</div>,
    }),
    // Add the Explain button column
    columnHelper.display({
      id: 'explain',
      cell: (info) => (
        <div className='h-12 flex items-center justify-end'>
          <button
            onClick={() => openModal(info.row.original)}
            className='px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700'
          >
            Explain
          </button>
        </div>
      ),
      header: () => <div className='' />, // Empty header
    }),
  ]

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
      columnVisibility: {
        // Only include the Finch column if the employer is on finch automated deductions
        finchStatus: employer?.payroll_config?.deduction_method === 'finch'
      }
    },
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: false,
  })

  if (isLoading || employerLoading) {
    return <div>Loading...</div>
  }

  if (error || employerError) {
    return <div>Error loading consistent deductions: {error?.message || 'Unable to load employer'}</div>
  }

  return (
    <>
      <TanstackTable
        table={table}
        tableClasses='overflow-hidden'
        tableMessageHeightClass='h-16'
        paginationLabelSingular='deduction'
      />

      <DeductionExplanationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        pretaxDeduction={selectedDeduction?.pretaxDeduction}
        posttaxDeduction={selectedDeduction?.posttaxDeduction}
      />
    </>
  )
}

// Helper function to determine the highest priority status
const getHighestPriorityStatus = (status1?: string, status2?: string): string => {
  const statusPriority = {
    created: 3,
    updated: 2,
    corrected: 1,
    unchanged: 0
  }

  const priority1 = status1 ? statusPriority[status1 as keyof typeof statusPriority] || 0 : 0
  const priority2 = status2 ? statusPriority[status2 as keyof typeof statusPriority] || 0 : 0

  if (priority1 >= priority2) {
    return status1 || 'unchanged'
  } else {
    return status2 || 'unchanged'
  }
}

type MemberConsistentDeductionsProps = {
  memberId: string
  employerId: string
  yearsAvailable: number[]
}

const MemberConsistentDeductions = ({ memberId, employerId, yearsAvailable }: MemberConsistentDeductionsProps) => {
  const [selectedYear, setSelectedYear] = useState<number>(
    yearsAvailable[0]
  )

  return (
    <>
      <div className='justify-between flex mb-4 items-center'>
        <h2 className='text-xl font-bold leading-tight text-gray-900'>
          Consistent Deductions
        </h2>
        <div className='w-fit'>
          <Select
            name='year' defaultValue={selectedYear} onChange={(year) => {
              setSelectedYear(parseInt(year.target.value))
            }}
          >
            {yearsAvailable.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </div>

      </div>
      <MemberConsistentDeductionsTableImpl
        memberId={memberId}
        employerId={employerId}
        selectedYear={selectedYear}
      />
    </>

  )
}

export default MemberConsistentDeductions
